import type { Dispatch, SetStateAction } from "react";
import type { YouTubePlayer } from "react-youtube";
import { create } from "zustand";
import type { Song } from "@/types/types";

export const EMPTY_SONG: Song = {
    id: "",
    title: "",
    artist: "",
    image: "",
    audioSrc: "",
    url: "",
    votes: 0,
    hot: false,
    platform: "",
    genre: "",
    duration: 0,
};

interface PlayerState {
    player: YouTubePlayer | null;
    song: Song;
    currentSong: Song | null;
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    isFullScreen: boolean;
    autoPlayNext: boolean;
    transitionAudioSrc: string;
    isTransitionPlaying: boolean;
    setPlayer: Dispatch<SetStateAction<YouTubePlayer | null>>;
    setSong: Dispatch<SetStateAction<Song>>;
    setCurrentSong: Dispatch<SetStateAction<Song | null>>;
    setIsPlaying: Dispatch<SetStateAction<boolean>>;
    setCurrentTime: Dispatch<SetStateAction<number>>;
    setDuration: Dispatch<SetStateAction<number>>;
    setVolume: Dispatch<SetStateAction<number>>;
    setIsFullScreen: Dispatch<SetStateAction<boolean>>;
    setAutoPlayNext: Dispatch<SetStateAction<boolean>>;
    setTransitionAudioSrc: (src: string) => void;
    setIsTransitionPlaying: (playing: boolean) => void;
}

const resolve = <T,>(value: SetStateAction<T>, current: T): T =>
    typeof value === "function" ? (value as (previous: T) => T)(current) : value;

export const usePlayerStore = create<PlayerState>((set, get) => ({
    player: null,
    song: EMPTY_SONG,
    currentSong: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    isFullScreen: false,
    autoPlayNext: false,
    transitionAudioSrc: "",
    isTransitionPlaying: false,
    setPlayer: (value) => set({ player: resolve(value, get().player) }),
    setSong: (value) => {
        const song = resolve(value, get().song);
        set({ song, currentSong: song });
    },
    setCurrentSong: (value) => {
        const currentSong = resolve(value, get().currentSong);
        set({ currentSong, song: currentSong ?? EMPTY_SONG });
    },
    setIsPlaying: (value) => set({ isPlaying: resolve(value, get().isPlaying) }),
    setCurrentTime: (value) => set({ currentTime: resolve(value, get().currentTime) }),
    setDuration: (value) => set({ duration: resolve(value, get().duration) }),
    setVolume: (value) => set({ volume: resolve(value, get().volume) }),
    setIsFullScreen: (value) => set({ isFullScreen: resolve(value, get().isFullScreen) }),
    setAutoPlayNext: (value) => set({ autoPlayNext: resolve(value, get().autoPlayNext) }),
    setTransitionAudioSrc: (transitionAudioSrc) => set({ transitionAudioSrc }),
    setIsTransitionPlaying: (isTransitionPlaying) => set({ isTransitionPlaying }),
}));
